import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementFrom,
  type FormListVariantType,
  type IoConfig,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
  type WithSlots,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithFeaturesConfigProps,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type FormListModelField,
  type FormListPregenerationType,
  type FormOrigin,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
} from '@blaze-shared/elements-domain';
import { DEFAULT_ARRAY_SCHEMA } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { FormListChildrenHandlerService } from './form-list-children-handler.service';
import { FormListValidationHandler } from './form-list-validation-handler.service';
import { FormListComponent } from './form-list.component';
import { FormListPropsBuilder } from './form-list.props-builder';
import { FormListPropsExtractor } from './form-list.props-extractor';
import { editableListV2Migration } from './migrations/002-form-list.migration';

export type FormListElement = CanvasElementFrom<'editableList'>;
export type FormDetailsListElement = CanvasElementFrom<'formDetailsList'>;
export type FormListLikeElement = CanvasElementFrom<'editableList' | 'formDetailsList'>;
export type FormListDef = WithProps<FormListPropsModel> &
  WithIoConfig &
  WithSlots<typeof FORM_LIST_ROW_TEMPLATE_SLOT_ID | typeof FORM_LIST_ACTIONS_SLOT_ID>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    /* 'editableList' is the original id of the component,
     * persisted in the layout json, so it can't be changed
     * without a proper compat logic in place.
     */
    editableList: FormListDef;
    formDetailsList: FormListDef;
  }
}

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 300;

export const FORM_LIST_ROW_TEMPLATE_SLOT_ID = '0';
export const FORM_LIST_ACTIONS_SLOT_ID = 'actions';

function getFormListDefinition(): CanvasElementDefinition<'editableList'> {
  return {
    type: 'editableList',
    componentType: FormListComponent,
    childrenHandler: FormListChildrenHandlerService,
    validationHandler: FormListValidationHandler,
    isListed: true,
    name: 'Form List',
    width: { mode: 'full', value: DEFAULT_WIDTH, fixed: false },
    height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
    slots: {
      ids: [FORM_LIST_ROW_TEMPLATE_SLOT_ID, FORM_LIST_ACTIONS_SLOT_ID],
      [FORM_LIST_ROW_TEMPLATE_SLOT_ID]: {
        children: [],
        alignment: { horizontal: 'left', vertical: 'middle', syncVerticalLineAlignment: true },
      },
      [FORM_LIST_ACTIONS_SLOT_ID]: {
        children: [],
        alignment: { horizontal: 'right', vertical: 'middle', syncVerticalLineAlignment: true },
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig('editableList'),
    initial: getInitialValues(),
    defaults: getDefaults(),
    propsBuilder: FormListPropsBuilder,
    propsExtractor: FormListPropsExtractor,
    groups: ['forms'],
    styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
    metaUiFeatures: { type: 'container' },
    features: {
      visualStyle: 'block',
    },
    version: 2,
    migrations: {
      2: editableListV2Migration,
    },
  };
}

registerElementDefinition(getFormListDefinition());

function getFormDetailsListDefinition(): CanvasElementDefinition<
  'formDetailsList',
  'editableList'
> {
  return {
    type: 'formDetailsList',
    variantOf: 'editableList',
    componentType: FormListComponent,
    isListed: false,
    name: 'Details View List',
    width: { mode: 'full', value: DEFAULT_WIDTH, fixed: false },
    height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
    slots: {
      ids: [FORM_LIST_ROW_TEMPLATE_SLOT_ID],
      [FORM_LIST_ROW_TEMPLATE_SLOT_ID]: {
        children: [],
        alignment: { horizontal: 'left', vertical: 'middle', syncVerticalLineAlignment: true },
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig('formDetailsList'),
    initial: getInitialValues(),
    defaults: getDefaults(),
    propsBuilder: FormListPropsBuilder,
    propsExtractor: FormListPropsExtractor,
    groups: ['forms'],
    styleIncludePaths: ['style'],
    metaUiFeatures: { type: 'container' },
    features: {
      visualStyle: 'block',
    },
    version: 2,
    migrations: {
      2: editableListV2Migration,
    },
  };
}

registerElementDefinition(getFormDetailsListDefinition());

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'change', schema: DEFAULT_ARRAY_SCHEMA }],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  CanvasElementWithFeaturesConfigProps &
  StyleConfigProps &
  VisibilityElementProps;

export type FormListPropsModel = PropsModel & {
  content: {
    origin: FormOrigin;
    pregenerationType?: FormListPregenerationType;
    options: {
      addFirstRowOnLoad?: boolean;
      showFieldNameAsHeader?: boolean;
      numberEntries?: boolean;
      showAddAction?: boolean;
      addActionLabel?: string;
      showDeleteAction?: boolean;
      hasMinRows?: number;
      hasMaxRows?: number;
      minRows?: number;
      maxRows?: number;
      noDataMessage?: string;
    };
    model: FormListModelField[];
    title: ElementLabelProps;
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
    titleBlock: ElementTitleStyleProps;
  };
};

function getInitialValues(): PartialDeep<PropsOf<'editableList'>> {
  return {
    content: {
      origin: 'drag',
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}

function getDefaults(): PropsOf<'editableList'> {
  return {
    content: {
      origin: 'drag',
      options: {
        showFieldNameAsHeader: true,
        numberEntries: true,
        showAddAction: true,
        addActionLabel: 'ADD NEW',
        showDeleteAction: true,
        addFirstRowOnLoad: true,
        minRows: 1,
        maxRows: 3,
      },
      model: [],
      title: {
        enabled: false,
        align: 'left',
        label: '',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingFormList' },
        fill: { color1: '$t.fillInternal', selectorType: 'none' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
    },
  };
}

function getPropsConfig(variant: FormListVariantType): PropertiesConfig {
  const isFormList = variant === 'editableList';
  const modelPath = 'content.model';
  const optionsFields: PropertiesConfigItemDefinition[] = [
    epc.fields.toggleField('content.options.showFieldNameAsHeader', 'Show field name as header'),
    epc.fields.toggleField('content.options.numberEntries', 'Number each entry'),
  ];
  if (isFormList) {
    optionsFields.unshift(
      epc.fields.toggleField('content.options.addFirstRowOnLoad', 'Add first row on load', {
        tooltip:
          'When enabled, adds the first row only once on component load if the list is empty.',
      }),
    );
    optionsFields.push(
      epc.utils.observedField(
        epc.fields.textInput('content.options.minRows', 'Min rows', {
          inputType: 'number',
          isOptional: 'content.options.hasMinRows',
          numberMin: 1,
          numberMax: 'content.options.maxRows',
          checkMin: true,
          checkMax: 'content.options.hasMaxRows',
        }),
        (props: FormListPropsModel, value: any, __, ___, ____, path: string) => {
          if (!path.endsWith('hasMinRows') || !value) {
            return {};
          }

          const { minRows, maxRows } = props.content.options;
          if (minRows === undefined || maxRows === undefined) {
            return {};
          }

          const minRowsN = +minRows;
          const maxRowsN = +maxRows;
          if (maxRowsN < minRowsN) {
            return { 'content.options.minRows': maxRowsN - 1 };
          }
          return {};
        },
      ),
      epc.utils.observedField(
        epc.fields.textInput('content.options.maxRows', 'Max rows', {
          inputType: 'number',
          isOptional: 'content.options.hasMaxRows',
          numberMin: 'content.options.minRows',
          checkMin: 'content.options.hasMinRows',
          checkMax: true,
        }),
        (props: FormListPropsModel, value: any, __, ___, ____, path: string) => {
          if (!path.endsWith('hasMaxRows') || !value) {
            return {};
          }

          const { minRows, maxRows } = props.content.options;
          if (minRows === undefined || maxRows === undefined) {
            return {};
          }

          const minRowsN = +minRows;
          const maxRowsN = +maxRows;
          if (minRowsN > maxRowsN) {
            return { 'content.options.maxRows': minRowsN + 1 };
          }
          return {};
        },
      ),
    );
  } else {
    optionsFields.push(epc.fields.textInput('content.options.noDataMessage', 'Empty message'));
  }

  const contentSubsections: PropertiesConfigSubsection[] = [
    epc.subsections.dataSubsection(
      'data.?key:value.enabled',
      '',
      [],
      [
        epc.utils.disabledField(
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Value',
            sources: 'list',
            bindings: [],
          }),
          (model: FormListPropsModel) =>
            ['form-group', 'wizard'].includes(model.content.origin)
              ? 'Data source cannot be modified in an auto-generated form.'
              : undefined,
        ),
        {
          path: modelPath,
          inputType: 'editableListFields',
          label: '',
          inputOptions: {
            dataSourcePath: 'data.?key:value.dataSource',
            fieldConfig: (index: number) => [
              epc.subsections.subsection(`${modelPath}.${index}`, 'Field Properties', [
                epc.fields.textInput(`${modelPath}.${index}.title`, 'Title'),
                epc.fields.selectFieldFromApi(
                  `${modelPath}.${index}.controlType`,
                  'Type',
                  'LIST_CONTROLS_FOR_FIELD',
                  {
                    showSelection: true,
                    allowNone: false,
                    modelData: `${modelPath}.${index}`,
                  },
                ),
                epc.fields.toggleField(`${modelPath}.${index}.visible`, 'Show Column'),
              ]),
            ],
          },
        } as PropertiesConfigItemDefinition<'editableListFields'>,
      ],
      {
        enabledByDefault: true,
        subsectionOptions: { hideLabel: true },
      },
    ),
    epc.subsections.subsection('content', 'Options', optionsFields),
  ];

  if (isFormList) {
    const actionsSubsection = epc.subsections.subsection('content', 'Actions', [
      epc.fields.toggleField('content.options.showAddAction', 'Add new row'),
      epc.utils.conditionalField(
        epc.fields.textInput('content.options.addActionLabel', 'Add label'),
        'content.options.showAddAction',
        (value) => value === false,
      ),
      epc.fields.toggleField('content.options.showDeleteAction', 'Delete row'),
    ]);
    contentSubsections.push(actionsSubsection);
  }

  contentSubsections.push(
    epc.subsections.visibilitySubsection(),
    epc.subsections.titleContentSubsection('content'),
  );

  const stylesSubsections: PropertiesConfigSubsection[] = [
    epc.subsections.styleSheetSubsection(),
    epc.subsections.subsection('style.general', 'Padding & Margin', [
      epc.fields.spacingAndAlignField('style.general.offset', {
        hasMargin: true,
        hasPadding: true,
      }),
    ]),

    epc.subsections.subsection('style.general', 'General', [
      ...(isFormList
        ? [
            epc.fields.colorField('style.general.accent', 'Accent', {
              allowNone: false,
              allowGradient: false,
            }),
          ]
        : [
            epc.fields.colorField('style.general.fill', 'Fill', {}),
            epc.fields.colorField('style.general.outline', 'Outline', { allowGradient: false }),
            epc.fields.sliderField('style.general.outlineWidth', 'Width', [
              { min: 0, max: 10, unit: 'px' },
            ]),
            epc.fields.radiusField('style.general.radius'),
          ]),
      ...epc.fields.visualEffectField('style.general.visualEffect'),
    ]),

    epc.subsections.titleStyleSubsection({
      enabled: 'content.title.enabled',
      color: 'style.general.title',
      fontFamily: 'style.titleBlock.fontFamily',
      fontWeight: 'style.titleBlock.fontWeight',
      fontStyle: 'style.titleBlock.fontStyle',
      fontSize: 'style.titleBlock.fontSize',
      horizontalAlign: 'content.title.align',
      icon: 'content.title.icon',
      iconColor: 'content.title.iconColor',
      iconPosition: 'content.title.iconPosition',
      iconSize: 'style.titleBlock.iconSize',
    }),
  ];

  return epc.initConfig(variant, [
    epc.section('content', 'Content', contentSubsections),
    epc.section('style', 'Style', stylesSubsections, true),
  ]);
}
